import { forwardRef, useImperativeHandle, useState } from 'react';
import { Controller, useFieldArray, useForm, UseFormSetValue, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Typography, NewDropdown } from 'app/components/ui';
import { i18n } from 'app/utils/i18n';
import { SummandScopingRow } from '../SummandScopingRow';

export const STANDARD_LABEL_TYPE = 'label' as const;
export const OTHER_LABEL_TYPES = ['terseLabel', 'verboseLabel', 'documentation'] as const;
export type LabelType = typeof STANDARD_LABEL_TYPE | (typeof OTHER_LABEL_TYPES)[number];

const ALL_LABEL_TYPES: readonly LabelType[] = [STANDARD_LABEL_TYPE, ...OTHER_LABEL_TYPES];

type LabelsByLang = Record<string, Partial<Record<LabelType, string>>>;

export type ExtensionKind = 'NEW' | 'SUB_TOTAL';

export interface SummandFormValue {
  conceptName: string;
  weight: 1 | -1;
  dimQualifiers?: { axisConceptId: string; memberConceptId: string }[];
}

export interface NewExtensionFormValues {
  labels: LabelsByLang;
  concept: string;
  description?: string;
  widerAnchor: string;
  narrowerAnchors?: string;
  dataType: string;
  balanceType: string;
  periodType: string;
  kind: ExtensionKind;
  summands: SummandFormValue[];
}

export type NewExtensionForm = NewExtensionFormValues;

export const newExtensionSchema = yup.object().shape({
  labels: yup.mixed<LabelsByLang>().test('at-least-one-standard-label', 'A standard label is required in at least one language', (value) => {
    if (!value || typeof value !== 'object') return false;
    return Object.values(value).some((byType) => {
      const text = byType?.[STANDARD_LABEL_TYPE];
      return typeof text === 'string' && text.trim().length > 0;
    });
  }),
  concept: yup
    .string()
    .matches(/^[A-Z][A-Za-z0-9]*$/, 'Concept name must be PascalCase (e.g. FlightEquipment)')
    .required('Concept is required'),
  description: yup.string().optional(),
  widerAnchor: yup.string().required('Wider Anchor is required'),
  narrowerAnchors: yup.string().optional(),
  dataType: yup.string().required('Data Type is required'),
  balanceType: yup.string().required('Balance Type is required'),
  periodType: yup.string().required('Period Type is required'),
  kind: yup.string().oneOf(['NEW', 'SUB_TOTAL']).required(),
  summands: yup.array().when('kind', {
    is: 'SUB_TOTAL',
    then: (s) =>
      s.min(1, 'Sub-total extensions need at least one summand').of(
        yup.object().shape({
          conceptName: yup.string().required('Pick a summand concept'),
          weight: yup.number().oneOf([1, -1]).required(),
        })
      ),
    otherwise: (s) => s.max(0),
  }),
});

export type NewExtensionFormComponentRef = {
  setValue: UseFormSetValue<NewExtensionFormValues>;
};

type NewExtensionFormComponentProps = {
  languages: string[];
  onSubmit: (data: NewExtensionFormValues) => void;
  onCancel: () => void;
  onOpenAnchorPicker: (type: 'wider' | 'narrower' | 'summand', selected: string | null, summandIndex?: number) => void;
  widerAnchorSeed?: string | null;
  documentSuperId?: number;
  defaultValues?: Partial<NewExtensionFormValues>;
  editMode?: boolean;
};

// Allowed XBRL item types. See backend ExtensionValidation.ts.
const dataTypeOptions = [
  { value: 'monetaryItemType', label: 'Monetary' },
  { value: 'decimalItemType', label: 'Decimal' },
  { value: 'integerItemType', label: 'Integer' },
  { value: 'percentItemType', label: 'Percent' },
  { value: 'sharesItemType', label: 'Shares' },
  { value: 'stringItemType', label: 'String' },
  { value: 'textBlockItemType', label: 'Text block' },
  { value: 'booleanItemType', label: 'Boolean' },
  { value: 'dateItemType', label: 'Date' },
  { value: 'gYearItemType', label: 'Year' },
  { value: 'fractionItemType', label: 'Fraction' },
];

const balanceTypeOptions = [
  { value: 'debit', label: 'Debit' },
  { value: 'credit', label: 'Credit' },
  { value: 'none', label: '—' },
];

const periodTypeOptions = [
  { value: 'instant', label: 'Instant' },
  { value: 'duration', label: 'Duration' },
];

function buildLabelsDefault(languages: string[]): LabelsByLang {
  const out: LabelsByLang = {};
  for (const lang of languages) {
    out[lang] = {};
    for (const type of ALL_LABEL_TYPES) {
      out[lang]![type] = '';
    }
  }
  return out;
}

export const NewExtensionFormComponent = forwardRef<NewExtensionFormComponentRef, NewExtensionFormComponentProps>(
  ({ languages, onSubmit, onCancel, onOpenAnchorPicker, widerAnchorSeed, documentSuperId, defaultValues, editMode }, ref) => {
    const [showOtherTypes, setShowOtherTypes] = useState(false);
    const [activeLang, setActiveLang] = useState<string>(languages[0] ?? '');

    const {
      handleSubmit,
      register,
      setValue,
      watch,
      control,
      formState: { errors },
    } = useForm<NewExtensionFormValues>({
      shouldUnregister: false,
      resolver: yupResolver(newExtensionSchema) as any,
      defaultValues: {
        labels: buildLabelsDefault(languages),
        description: '',
        narrowerAnchors: '',
        widerAnchor: widerAnchorSeed ?? undefined,
        balanceType: 'none',
        periodType: 'duration',
        dataType: 'monetaryItemType',
        kind: 'NEW',
        summands: [],
        ...(defaultValues ?? {}),
      },
    });

    useImperativeHandle(ref, () => ({ setValue }));

    const widerAnchor = watch('widerAnchor');
    const narrowerAnchors = watch('narrowerAnchors');
    const kind = useWatch({ control, name: 'kind' });
    const { fields: summandFields, append: appendSummand, remove: removeSummand } = useFieldArray({ control, name: 'summands' });

    const labelTypeI18n: Record<LabelType, string> = {
      label: i18n('tag.extension.create.label.standard'),
      terseLabel: i18n('tag.extension.create.label.terse'),
      verboseLabel: i18n('tag.extension.create.label.verbose'),
      documentation: i18n('tag.extension.create.label.documentation'),
    };

    return (
      <form id="new-extension-form" onSubmit={handleSubmit(onSubmit)} className="new-extension__form">
        <div className="input-group">
          <div role="tablist" aria-label="Label languages" className="new-extension__lang-tabs">
            {languages.map((lang) => {
              const standardForLang = watch(`labels.${lang}.${STANDARD_LABEL_TYPE}` as any) as string | undefined;
              const filled = !!(standardForLang && standardForLang.trim().length > 0);
              const isActive = lang === activeLang;
              return (
                <button
                  key={lang}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  className="btn btn-sm"
                  onClick={() => setActiveLang(lang)}
                  style={{
                    textTransform: 'uppercase',
                    marginRight: 4,
                    fontWeight: 600,
                    border: '1px solid #cfd2d7',
                    backgroundColor: isActive ? '#37835f' : '#ffffff',
                    color: isActive ? '#ffffff' : '#37353B',
                  }}
                >
                  {lang}
                  {filled && (
                    <span aria-label="filled" style={{ marginLeft: 6, color: isActive ? '#ffffff' : '#37835f' }}>
                      •
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <div className="mt-2">
            <Typography variant="body-7" fontWeight="semibold">
              {labelTypeI18n.label} <span className="required">*</span>
            </Typography>
            {activeLang && (
              <input
                key={`label-${activeLang}`}
                {...register(`labels.${activeLang}.${STANDARD_LABEL_TYPE}` as any)}
                className="input-field"
                aria-label={`Standard label (${activeLang})`}
              />
            )}
            {(errors as any).labels?.message && (
              <Typography variant="body-3" color="red">
                {(errors as any).labels.message}
              </Typography>
            )}
          </div>

          <button type="button" className="btn btn-link p-0 mt-2" onClick={() => setShowOtherTypes((s) => !s)} aria-expanded={showOtherTypes}>
            {showOtherTypes ? i18n('tag.extension.create.label.hideOtherTypes') : i18n('tag.extension.create.label.showOtherTypes')}
          </button>
          {showOtherTypes &&
            activeLang &&
            OTHER_LABEL_TYPES.map((type) => (
              <div key={type} className="mt-2">
                <Typography variant="body-7" fontWeight="semibold">
                  {labelTypeI18n[type]}
                </Typography>
                <input
                  key={`${type}-${activeLang}`}
                  {...register(`labels.${activeLang}.${type}` as any)}
                  className="input-field"
                  aria-label={`${labelTypeI18n[type]} (${activeLang})`}
                />
              </div>
            ))}
        </div>

        <div className="input-group">
          <Typography variant="body-7" fontWeight="semibold" aria-label="Concept">
            {i18n('tag.extension.create.concept')} <span className="required">*</span>
          </Typography>
          <input {...register('concept')} className="input-field" placeholder="FlightEquipment" />
          {errors.concept && (
            <Typography variant="body-3" color="red">
              {errors.concept.message}
            </Typography>
          )}
        </div>

        <div className="input-group">
          <Typography variant="body-7" fontWeight="semibold" aria-label="Description">
            {i18n('tag.extension.create.description')}
          </Typography>
          <textarea {...register('description')} className="input-field" />
        </div>

        <div className="input-group">
          <Typography variant="body-7" fontWeight="semibold" aria-label="Wider Anchor">
            {i18n('tag.extension.create.wider.anchor')} <span className="required">*</span>
          </Typography>
          <div className="flex-row" onClick={() => onOpenAnchorPicker('wider', widerAnchor ?? null)}>
            <input {...register('widerAnchor')} value={widerAnchor || ''} className="input-field pr-3" readOnly placeholder="Search taxonomy" />
            <span className="search-icon">
              <i className="fa fa-search" />
            </span>
          </div>
          {errors.widerAnchor && (
            <Typography variant="body-3" color="red">
              {errors.widerAnchor.message}
            </Typography>
          )}
        </div>

        <div className="input-group">
          <Typography variant="body-7" fontWeight="semibold" aria-label="Narrower Anchor(s)">
            {i18n('tag.extension.create.narrower.anchor')}
          </Typography>
          <div className="flex-row" onClick={() => onOpenAnchorPicker('narrower', narrowerAnchors ?? null)}>
            <input {...register('narrowerAnchors')} value={narrowerAnchors || ''} readOnly placeholder="Search taxonomy" className="input-field pr-3" />
            <span className="search-icon">
              <i className="fa fa-search" />
            </span>
          </div>
        </div>

        <div className="define-group">
          <Typography variant="body-7" fontWeight="light">
            {i18n('tag.extension.create.define.additional.properties')}
            <span className="divider" />
          </Typography>

          <Controller
            name="dataType"
            control={control}
            render={({ field }) => (
              <div className="grid-col-2">
                <Typography variant="body-5" fontWeight="semibold" aria-label="Data Type">
                  {i18n('tag.extension.create.data.type')}
                </Typography>
                <NewDropdown currentValue={field.value ?? 'monetaryItemType'} options={dataTypeOptions} onUpdate={(v: string) => field.onChange(v)} />
              </div>
            )}
          />

          <Controller
            name="balanceType"
            control={control}
            render={({ field }) => (
              <div className="grid-col-2">
                <Typography variant="body-5" fontWeight="semibold" aria-label="Balance Type">
                  {i18n('tag.extension.create.balance.type')}
                </Typography>
                <NewDropdown currentValue={field.value ?? 'none'} options={balanceTypeOptions} onUpdate={(v: string) => field.onChange(v)} />
              </div>
            )}
          />

          <Controller
            name="periodType"
            control={control}
            render={({ field }) => (
              <div className="grid-col-2">
                <Typography variant="body-5" fontWeight="semibold" aria-label="Period Type">
                  {i18n('tag.extension.create.period.type')}
                </Typography>
                <NewDropdown currentValue={field.value ?? 'duration'} options={periodTypeOptions} onUpdate={(v: string) => field.onChange(v)} />
              </div>
            )}
          />
        </div>

        <div className="input-group">
          <Controller
            name="kind"
            control={control}
            render={({ field }) => (
              <div className="grid-col-2">
                <Typography variant="body-7" fontWeight="semibold" aria-label="Extension Kind">
                  {i18n('tag.extension.create.kind')}
                </Typography>
                <NewDropdown
                  currentValue={field.value ?? 'NEW'}
                  options={[
                    { value: 'NEW', label: i18n('tag.extension.create.kind.new') },
                    { value: 'SUB_TOTAL', label: i18n('tag.extension.create.kind.subTotal') },
                  ]}
                  onUpdate={(v: string) => field.onChange(v)}
                />
              </div>
            )}
          />
        </div>

        {kind === 'SUB_TOTAL' && (
          <div className="input-group">
            <Typography variant="body-7" fontWeight="semibold">
              {i18n('tag.extension.create.summands')}
            </Typography>
            {summandFields.map((row, idx) => {
              const conceptName = (watch(`summands.${idx}.conceptName` as const) as string | undefined) ?? '';
              return (
                <div key={row.id} style={{ marginBottom: 8 }}>
                  <div className="flex-row" style={{ gap: 8, alignItems: 'center' }}>
                    <div className="flex-row" style={{ flex: 1 }} onClick={() => onOpenAnchorPicker('summand', conceptName || null, idx)}>
                      <input
                        {...register(`summands.${idx}.conceptName` as const)}
                        value={conceptName}
                        readOnly
                        placeholder={i18n('tag.extension.create.summand.search')}
                        className="input-field pr-3"
                        aria-label={`Summand ${idx + 1} concept`}
                      />
                      <span className="search-icon">
                        <i className="fa fa-search" />
                      </span>
                    </div>
                    <Controller
                      name={`summands.${idx}.weight` as const}
                      control={control}
                      render={({ field }) => (
                        <NewDropdown
                          currentValue={String(field.value ?? 1)}
                          options={[
                            { value: '1', label: '+1' },
                            { value: '-1', label: '−1' },
                          ]}
                          onUpdate={(v: string) => field.onChange(v === '-1' ? -1 : 1)}
                        />
                      )}
                    />
                    <button type="button" className="btn btn-link" onClick={() => removeSummand(idx)} aria-label={`Remove summand ${idx + 1}`}>
                      <i className="fa fa-times" />
                    </button>
                  </div>
                  <Controller
                    control={control}
                    name={`summands.${idx}.dimQualifiers` as const}
                    render={({ field }) => (
                      <SummandScopingRow
                        summandConceptName={conceptName}
                        documentSuperId={documentSuperId}
                        value={(field.value ?? []) as { axisConceptId: string; memberConceptId: string }[]}
                        onChange={field.onChange}
                      />
                    )}
                  />
                </div>
              );
            })}
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => appendSummand({ conceptName: '', weight: 1 })}
              aria-label={i18n('tag.extension.create.addSummand')}
            >
              {i18n('tag.extension.create.addSummand')}
            </button>
          </div>
        )}

        {Object.keys(errors).length > 0 && (
          <div className="new-extension__form-errors" role="alert">
            {Object.entries(errors).map(([field, err]) => (
              <Typography key={field} variant="body-3" color="red">
                {field}: {(err as any)?.message ?? 'Invalid value'}
              </Typography>
            ))}
          </div>
        )}
      </form>
    );
  }
);
